// Cálculo de horas contratadas a partir de franjas horarias por objetivo
// ("Datos del servicio contratado"). Mismo criterio que el backend
// (server.py: _shifts_daily_totals / _shifts_avg_daily_hours / _shifts_is_24h)
// para mostrar el total en vivo mientras se edita, antes de guardar.
export const WEEKDAY_CODES = ["lun", "mar", "mie", "jue", "vie", "sab", "dom"];
export const WEEKDAY_LABELS = { lun: "Lun", mar: "Mar", mie: "Mié", jue: "Jue", vie: "Vie", sab: "Sáb", dom: "Dom" };

export function shiftHours(shift) {
  if (!shift?.time_from || !shift?.time_to) return 0;
  const [h1, m1] = shift.time_from.split(":").map(Number);
  const [h2, m2] = shift.time_to.split(":").map(Number);
  let diff = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (diff <= 0) diff += 24 * 60;
  const staff = Math.max(1, Number(shift.staff_count) || 1);
  return Math.round((diff / 60) * staff * 100) / 100;
}

function shiftApplies(shift, code) {
  const dow = shift.days_of_week;
  return !dow || dow.length === 0 || dow.includes(code);
}

export function dailyTotals(shifts) {
  const totals = {};
  WEEKDAY_CODES.forEach((c) => { totals[c] = 0; });
  (shifts || []).forEach((s) => {
    const h = shiftHours(s);
    WEEKDAY_CODES.forEach((c) => { if (shiftApplies(s, c)) totals[c] += h; });
  });
  return totals;
}

// Fix BU (2026-11-04): horas del servicio en un mes (cada día suma las franjas que aplican ese día de la
// semana). Mismo criterio que el presupuesto en el backend (budget.py: _shift_breakdown).
export function monthlyHours(shifts, year, month) {
  if (!shifts || shifts.length === 0 || !year || !month) return 0;
  const totals = dailyTotals(shifts);
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  let sum = 0;
  for (let d = 1; d <= days; d++) {
    const wd = new Date(Date.UTC(year, month - 1, d)).getUTCDay(); // 0 = domingo
    sum += totals[WEEKDAY_CODES[(wd + 6) % 7]];
  }
  return Math.round(sum * 100) / 100;
}

export function avgDailyHours(shifts) {
  if (!shifts || shifts.length === 0) return null;
  const totals = dailyTotals(shifts);
  const sum = WEEKDAY_CODES.reduce((a, c) => a + totals[c], 0);
  return Math.round((sum / 7) * 100) / 100;
}

function mergeIntervals(intervals) {
  if (!intervals.length) return [];
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const merged = [[...sorted[0]]];
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i];
    if (s <= merged[merged.length - 1][1]) merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    else merged.push([s, e]);
  }
  return merged;
}

export function is24hCoverage(shifts) {
  if (!shifts || shifts.length === 0) return false;
  for (const c of WEEKDAY_CODES) {
    const intervals = [];
    (shifts || []).forEach((s) => {
      if (!shiftApplies(s, c) || !s.time_from || !s.time_to) return;
      const [h1, m1] = s.time_from.split(":").map(Number);
      const [h2, m2] = s.time_to.split(":").map(Number);
      const start = h1 * 60 + m1, end = h2 * 60 + m2;
      if (end <= start) { intervals.push([start, 24 * 60]); intervals.push([0, end]); }
      else intervals.push([start, end]);
    });
    const merged = mergeIntervals(intervals);
    if (!(merged.length === 1 && merged[0][0] <= 0 && merged[0][1] >= 24 * 60)) return false;
  }
  return true;
}

export function newEmptyShift() {
  return { id: `tmp_${Math.random().toString(36).slice(2)}`, label: "", time_from: "", time_to: "", staff_count: 1, days_of_week: null };
}

// ---- Horarios por sede: una sede sin franjas propias hereda/muestra las
// del cliente (para no obligar a cargar dos veces cuando el esquema es el
// mismo en todas las sedes). Estas funciones calculan el total "real" del
// objetivo sumando las sedes cuando alguna tiene horario propio configurado.
export function siteHasOwnSchedule(site) {
  return !!(site?.contract_shifts && site.contract_shifts.length > 0);
}

export function siteEffectiveShifts(site, clientShifts) {
  return siteHasOwnSchedule(site) ? site.contract_shifts : (clientShifts || []);
}

export function siteEffectiveTotal(site, clientTotal, clientShifts) {
  if (siteHasOwnSchedule(site)) {
    return site.contract_total_hours ?? avgDailyHours(site.contract_shifts);
  }
  return clientTotal ?? avgDailyHours(clientShifts);
}

export function sitesHaveOwnSchedule(sites) {
  return (sites || []).some(siteHasOwnSchedule);
}

// Total real del objetivo sumando sedes: null si ninguna sede tiene horario
// propio (en ese caso se sigue mostrando solo el total a nivel cliente, sin
// cambios respecto al comportamiento anterior).
export function sumSitesRealTotal(sites, clientTotal, clientShifts) {
  if (!sitesHaveOwnSchedule(sites)) return null;
  const sum = (sites || []).reduce((acc, s) => acc + (siteEffectiveTotal(s, clientTotal, clientShifts) || 0), 0);
  return Math.round(sum * 100) / 100;
}
